import React from 'react';
import { Clear as ClearIcon, Check as CheckIcon } from '@material-ui/icons';
import { AclPermissionsRole, Permissions } from '@eventmanager/types';

import { useAppSelector } from '../../hooks';
import { currentUserPermissions, getPermission } from '../../selectors';
import { c } from '../../utils';

// Prefixes a string with "No " if a feature is disabled
const boolPrefix = (isEnabled: boolean) => (!isEnabled ? 'No ' : '');

// Converts numerical values into readable time strings (minutes/hours)
const formatTime = (value: number) =>
  value < 1 ? `${Math.floor(value * 60)} minute` : `${value} hour`;

// Map of permission keys to their display string formatters.
// Handles infinity symbols (-1) and boolean-to-text conversion.
const planDetails: Partial<
  Record<keyof Permissions, (value: any, role?: AclPermissionsRole) => string>
> = {
  /*aiGeneratedEvents: (value: number) => {
    return `${value !== 0 ? '' : value} AI Generated Events`;
  },*/
  maxAttendees: (value: number) =>
    `Up to ${value === -1 ? '∞' : value} Event Attendees`,
  maxFloors: (value: number) =>
    `Up to ${value === -1 ? '∞' : value} Floors per Event`,
  maxEventDuration: (value: number) =>
    `Up to ${value === -1 ? '∞' : formatTime(value)} per Event`,
  maxConcurrentEvents: (value: number) =>
    `${value === -1 ? '∞' : value} Concurrent Event`,
  // [2026-04-01] Options toggled based on the user's current subscription plan.
  mobileEventAccess: (isEnabled: boolean) =>
    `${boolPrefix(isEnabled)}Mobile Event Access`,
  landingPageCustomization: (isEnabled: boolean) =>
    `${boolPrefix(isEnabled)}Custom Event Landing Page`,
  broadcastRecordings: (isEnabled: boolean) =>
    `${boolPrefix(isEnabled)}Broadcast Recordings`,
  dataAnalytics: (isEnabled: boolean) =>
    `${boolPrefix(isEnabled)}Post-Event Data Analytics`,
  customMaps: (isEnabled: boolean) => `${boolPrefix(isEnabled)}Custom Maps`,
  attendeeTracking: (trackingId: string) =>
    `${boolPrefix(!!trackingId)}Attendee Tracking`,
  dedicatedSupportAgent: (isEnabled: boolean) =>
    `${boolPrefix(isEnabled)}Dedicated Support Agent`,
  whiteLabeling: (isEnabled: boolean) =>
    `${boolPrefix(isEnabled)}White Labeling`,
  readySecurity: (isEnabled: boolean) =>
    `${boolPrefix(isEnabled)}Enterprise Ready Security`,
};

// Transforms raw permission data into an array of UI-ready objects.
// Filters out keys based on the provided 'filter' array.
function getPlanDetails(
  role: AclPermissionsRole,
  permissions: Permissions,
  filter: string[],
): PlanDetailProps[] {
  const shownKeys = Object.keys(planDetails).filter(
    key => !filter.includes(key),
  );
  return shownKeys.map(key => {
    const value = permissions[key];
    return {
      key,
      isActive: !!value,
      text: planDetails[key](value, role),
    };
  });
}

interface PlanDetailProps {
  key: string;
  isActive: boolean;
  isOverride?: boolean;
  text: string;
}

// Individual list item displaying a specific plan feature and its status icon
function PlanDetail({ isActive, text }: PlanDetailProps) {
  const Icon = isActive ? CheckIcon : ClearIcon;
  return (
    <li
      className={c(
        'color-shade-80 flex items-center p_25',
        !isActive && 'opacity-0_5',
      )}
    >
      <Icon className="mr1" />
      {text}
    </li>
  );
}

interface PlanDetailsProps {
  role?: AclPermissionsRole;
  filter?: string[];
}

// [2026-03-08 ]Main Component: Displays a list of features for a specific role or the current user.
// It automatically fetches permissions and identifies overridden values.
export function PlanDetails({ role, filter = [] }: PlanDetailsProps) {
  const { permissions } = role
    ? {
        permissions: useAppSelector(getPermission(role)),
      }
    : useAppSelector(currentUserPermissions);
  if (!permissions) return null;
  const details = getPlanDetails(permissions.role, permissions, filter);
  return (
    <ul className="list-style-none detail">
      {details.map(({ isActive, isOverride, text }, index) => (
        <PlanDetail
          key={`plan-${role}-detail-${index}`}
          isActive={isActive}
          isOverride={isOverride}
          text={text}
        />
      ))}
    </ul>
  );
}
